/* eslint-disable */
// Todo: add async validation
// Todo: handle radio buttons (validation) as a group instead of separate form-items
// TODO: check es lint errors
import Observer from 'utils/Observer';
import { closest, insertAfter } from 'utils/DOMHelpers';

//import FormField from 'form/FormField';

const DATA_FILTER_ATTR = 'data-filter-pattern',
  DATA_VALID_ATTR = 'aria-invalid',
  DATA_VALID_VAL = 'false',
  DATA_INVALID_VAL = 'true',
  DATA_MATCHES_ATTR = 'data-matches',
  DATA_ERROR_ATTR = 'data-error',
  DATA_ERROR_CLASS = 'form-item-error',
  DATA_REQUIRED_ATTR = 'data-required',
  DATA_FOCUS = 'data-focus';

class FormItem {
  constructor(element, options) {
    this._element = element;
    this._options = options;

    // Form items also create a form field, to enable the use of data/ApiValue anywhere
    //new FormField(this._element, options);

    this._initialize();
  }

  /**
   * Initialize form-item component
   * @static
   * @private
   */

  _initialize() {
    this._label = null;

    // Has an existing error label
    if (
      this._element.nextElementSibling &&
      this._element.nextElementSibling.classList.contains(DATA_ERROR_CLASS)
    ) {
      this._label = this._element.nextElementSibling;
    }

    // Regexes
    this._validateRegex = new RegExp(this._element.getAttribute('pattern'));
    if (this._element.hasAttribute(DATA_FILTER_ATTR)) {
      this._filterRegex = new RegExp(this._element.getAttribute(DATA_FILTER_ATTR), 'g');
    }

    // Element to match value against
    this._matchingElement = this._element.hasAttribute(DATA_MATCHES_ATTR)
      ? document.getElementById(this._element.getAttribute(DATA_MATCHES_ATTR))
      : null;

    // Messages
    this._errorMessage = this._element.getAttribute(DATA_ERROR_ATTR);
    this._requiredMessage = this._element.getAttribute(DATA_REQUIRED_ATTR) || 'Verplicht veld';

    // Bind events
    this._bindEvents();
  }

  /**
   * Bind events for the module
   * @static
   * @private
   */

  _bindEvents() {
    this._element.addEventListener('change', (e) => {
      if (e.target.value !== '') {
        this._onChange();
      }
    });

    this._element.addEventListener('focus', (e) => {
      this._onFocus();
    });

    this._element.addEventListener('focusout', (e) => {
      this._onFocusOut();
    });

    this._element.addEventListener('keyup', (e) => {
      this._onKeyUp();
    });

    this._element.addEventListener('paste', (e) => {
      this._onPaste();
    });

    document.addEventListener('DOMContentLoaded', (e) => {
      this._element._onLoad();
    });

    this._element.addEventListener('input', (e) => this._onInput());

    // Also bind to the element we match against
    this._matchingElement &&
      this._matchingElement.addEventListener('input', this._onMatchingElementInput.bind(this));
  }

  /**
   * Validate this form-item
   * @static
   * @private
   */

  _onLoad() {
    this.setTextarea();
    this.validate();
    Observer.publish(document, 'DOMContentLoaded');
  }

  _onChange() {
    this.setTextarea();
    this.validate();
    Observer.publish(this, 'change');
  }

  _onFocus() {
    this.setFocus();
    Observer.publish(this, 'focus');
  }

  _onFocusOut() {
    this.setEmpty();
    Observer.publish(this, 'focusout');
  }

  _onKeyUp() {
    this.setTextarea();
    Observer.publish(this, 'keyup');
  }

  _onPaste() {
    this.setTextarea();
    Observer.publish(this, 'paste');
  }

  /**
   * Validate this form-item (only to valid, not invalid)
   * @static
   * @private
   */

  _onInput() {
    this.isValid() && this.setValid();
    Observer.publish(this, 'change');
  }

  /**
   * Validate this form-item when the matching element input changes
   * @static
   * @public
   */

  _onMatchingElementInput() {
    this._element.value && this.validate();
  }

  /**
   * Public function to set the validation status of this form-item
   * @static
   * @public
   */

  validate() {
    if (this.isIgnored()) {
      return;
    }
    this.isValid() ? this.setValid() : this.setInvalid();
  }

  /**
   * Checks this form item for its validity
   * @returns {Boolean} Valid state of this form item
   * @static
   * @public
   */

  isValid() {
    if (this._filterRegex) {
      // When a filter regex is given, filter first then check the validate regex
      let filteredValue = this._element.value.replace(this._filterRegex, '');
      return this._validateRegex.test(filteredValue);
    }

    if (this._matchingElement) {
      return this._element.value === this._matchingElement.value && this._element.value !== '';
    }

    // Return the html5 form item valid state
    return this._element.checkValidity();
  }

  /**
   * Whether this input should be ignored when validating the form, because it's not required AND empty
   * @returns {Boolean} Required
   * @static
   * @public
   */

  isIgnored() {
    return this._element.value === '' && !this._element.hasAttribute('required');
  }

  /**
   * Whether this input should be ignored when the parent container is hidden
   * @returns {Boolean} Required
   * @static
   * @public
   */

  isHidden() {
    return !!closest(this._element, '[aria-hidden="true"]');
  }

  /**
   * Set state of this form-item
   * @static
   * @public
   */

  setValid() {
    if (!this._label && this._element.type === 'radio') {
      this._label = this._getRadioErrorLabel();
    }

    if (this._label) {
      this._label.parentNode.removeChild(this._label);
      this._label = null;
    }

    if (this._element.value) {
      // We entered a valid value
      this._element.setAttribute(DATA_VALID_ATTR, DATA_VALID_VAL);
    } else {
      // We entered nothing, but the field wasn't required
      this._element.removeAttribute(DATA_VALID_ATTR);
    }
  }

  /**
   * Set invalid state of this form-item
   * @static
   * @public
   */

  setInvalid() {
    let error;

    // Set element state to invalid
    this._element.setAttribute(DATA_VALID_ATTR, DATA_INVALID_VAL);
    // Empty && invalid -> Fields is not filled, required?
    // Just invalid -> Format doesn't match requested format
    if (this._element.type !== 'radio' && this._element.value) {
      error = this._errorMessage;
    } else if (this._element.hasAttribute('required')) {
      error = this._requiredMessage;
    }

    // If our error message is empty, don't attach an error element
    // The timeout enables other form events to be handled before displaying the error messages
    error && setTimeout(() => this._setErrorMessage(error), 1);
  }

  /**
   * Set form-item focus state on focus
   * @static
   * @public
   */

  setFocus() {
    this._element.parentNode.setAttribute(DATA_FOCUS, DATA_INVALID_VAL);
  }

  /**
   * Keep focus state for non empty form-item
   * @static
   * @public
   */
  setEmpty() {
    if (this._element.value) {
      this._element.parentNode.setAttribute(DATA_FOCUS, DATA_INVALID_VAL);
    } else {
      this._element.parentNode.setAttribute(DATA_FOCUS, DATA_VALID_VAL);
    }
  }

  setTextarea() {
    if (this._element.type === 'textarea') {
      let compStyles = window.getComputedStyle(this._element);
      const lineheight = parseInt(compStyles.getPropertyValue('line-height'), 10);
      const fontsize = parseInt(compStyles.getPropertyValue('font-size'), 10);
      const paddingtop = parseInt(compStyles.getPropertyValue('padding-top'), 10);
      const paddingbottom = parseInt(compStyles.getPropertyValue('padding-bottom'), 10);
      const lines = this._element.value.split('\n').length;
      this._element.style.height =
        lines * lineheight + lineheight + paddingtop + paddingbottom + 'px';
    }
  }

  /**
   * Retrieve the error label for a radio type input (grouped)
   * @returns {Element}
   * @private
   */
  _getRadioErrorLabel() {
    let parent = closest(this._element, '.form-items-radio');

    if (parent) {
      return parent.querySelector('.' + DATA_ERROR_CLASS);
    }
  }

  /**
   * Update or create the error message
   * @param {String} error - The error message to display
   * @private
   *
   */
  _setErrorMessage(error) {
    if (!this._label && this._element.type === 'radio') {
      this._label = this._getRadioErrorLabel();
    }

    // Update the existing error element, if there is one
    if (this._label) {
      this._label.textContent = error;
      return; // The bouncer
    }

    // Create a new error element
    this._label = document.createElement('label');
    this._label.setAttribute('for', this._element.getAttribute('id'));
    this._label.className = DATA_ERROR_CLASS;
    this._label.textContent = error;

    // Append it
    if (this._element.type === 'checkbox') {
      this._element.parentNode.appendChild(this._label);
    } else if (this._element.type === 'radio') {
      closest(this._element, '.form-items-radio').appendChild(this._label);
    } else {
      insertAfter(this._label, this._element);
    }
  }

  /**
   * Get the element for this form-item.
   * @return {Element}
   * @public
   *
   */

  getItem() {
    return this._element;
  }
}

export default FormItem;
/* eslint-enable */
